import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Input, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import {
  EditOutlined,
  EllipsisOutlined,
  SearchOutlined,
  UserDeleteOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";

import vivoLogoWhite from "@/assets/icons/vivo_logo_white.svg";
import GuideButton from "@/components/UI/GuideButton";
import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import PageHeader from "@/components/UI/PageHeader";
import { FetchWrapperState } from "@/components/UI/FetchWraper";
import { useAppNotification } from "@/hooks/useAppNotification";
import {
  useAdminManagedTrials,
  useAdminManagedUsers,
  useCreateManagedUser,
} from "@/hooks/useAdminManageUsers";
import {
  buildCreateManagedUserPayload,
  buildStudyOptionsFromManagedTrials,
  buildTrialRolesById,
  getAdminUserDisplayName,
  mapManagedUsersToAdminUsers,
} from "./adminRolesPermissionsMappers";
import EditUserSettingsModal from "./EditUserSettingsModal";
import DeactivateUserModal from "./DeactivateUserModal";
import { useManagedUserEditFlow } from "./useManagedUserEditFlow";
import BatchInviteUsersModal, {
  type BatchInvitePayload,
  type BatchInviteSubmitResult,
} from "./BatchInviteUsersModal";
import type { AdminAccountType, AdminUser } from "./adminUsersAccessData";
import AdminDownloadMenu from "./AdminDownloadMenu";
import {
  buildInviteErrorNotification,
  buildInvitePartialErrorNotification,
  buildInviteSuccessNotification,
} from "./adminNotificationConfigs";

const USERS_ACCESS_TOOLTIP =
  "Invite, edit, and deactivate people in your tenant here. Use Roles & Permissions to assign each user’s study access and role.";

function AccountBadge({ accountType }: { accountType: AdminAccountType }) {
  const isAdmin = accountType === "ADMIN";

  return (
    <span
      className="inline-flex items-center justify-center rounded border px-[3px] py-[2px] text-xs font-medium"
      style={
        isAdmin
          ? {
              backgroundColor: "#F0FAF7",
              borderColor: "#CAE6D6",
              color: "#004A42",
            }
          : {
              backgroundColor: "#EFF1F2",
              borderColor: "#0000001f",
              color: "#556C75",
            }
      }
    >
      {accountType}
    </span>
  );
}

function textSearchFilter<T>(
  selector: (record: T) => string,
): Partial<ColumnsType<T>[number]> {
  return {
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#0C98D0" : undefined }} />
    ),
    onFilter: (value, record) =>
      selector(record).toLowerCase().includes(String(value).toLowerCase()),
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        className="flex flex-col gap-2 p-2"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          autoFocus
          placeholder="Search"
          value={selectedKeys[0]}
          onChange={(event) =>
            setSelectedKeys(event.target.value ? [event.target.value] : [])
          }
          onPressEnter={() => confirm()}
        />
        <div className="flex justify-between gap-2">
          <Button
            size="small"
            disabled={selectedKeys.length === 0}
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
          >
            Reset
          </Button>
          <Button size="small" type="primary" onClick={() => confirm()}>
            Search
          </Button>
        </div>
      </div>
    ),
  };
}

function getUserSecondaryLine(user: AdminUser) {
  const name = `${user.firstName} ${user.lastName}`.trim();

  if (!name) {
    return null;
  }

  return user.email;
}

function getUserRowActionLabel(user: AdminUser) {
  return getAdminUserDisplayName(user);
}

export default function AdminUsersAccessPage() {
  const managedUsersQuery = useAdminManagedUsers();
  const managedTrialsQuery = useAdminManagedTrials();
  const createManagedUserMutation = useCreateManagedUser();
  const {
    contextHolder: editFlowContextHolder,
    editingUser,
    editErrorMessage,
    deactivationIntent,
    deactivateErrorMessage,
    isEditUserSubmitting,
    isDeactivateSubmitting,
    openEditModal,
    closeEditModal,
    requestDeactivation,
    closeDeactivationIntent,
    handleEditUserSubmit,
    handleDeactivateUser,
  } = useManagedUserEditFlow();
  const users = useMemo(
    () =>
      mapManagedUsersToAdminUsers(
        managedUsersQuery.data ?? [],
        managedTrialsQuery.data ?? [],
      ),
    [managedUsersQuery.data, managedTrialsQuery.data],
  );
  const managedStudyOptions = useMemo(
    () => buildStudyOptionsFromManagedTrials(managedTrialsQuery.data ?? []),
    [managedTrialsQuery.data],
  );
  const roleOptionsByTrialId = useMemo(
    () => buildTrialRolesById(managedTrialsQuery.data ?? []),
    [managedTrialsQuery.data],
  );
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();
  const [inviteErrorMessage, setInviteErrorMessage] = useState<string | null>(
    null,
  );
  const [visibleUsers, setVisibleUsers] = useState<AdminUser[]>(users);
  const hasActiveTableFilterRef = useRef(false);

  useEffect(() => {
    if (!hasActiveTableFilterRef.current) {
      setVisibleUsers(users);
    }
  }, [users]);

  const adminCount = useMemo(
    () => (managedUsersQuery.data ?? []).filter((user) => user.is_admin).length,
    [managedUsersQuery.data],
  );
  const isPageLoading =
    managedUsersQuery.isLoading || managedTrialsQuery.isLoading;
  const pageError = managedUsersQuery.error || managedTrialsQuery.error;

  const invitedByFilters = useMemo(() => {
    const names = Array.from(new Set(users.map((user) => user.invitedBy.name)));
    return names.map((name) => ({ text: name, value: name }));
  }, [users]);

  const buildExportRows = (rows: AdminUser[]) =>
    rows.map((user) => ({
      user: getAdminUserDisplayName(user),
      email: user.email,
      account_type: user.accountType,
      invited_on: user.invitedOn,
      invited_by: user.invitedBy.name,
      study_access: user.studyPermissions.length
        ? user.studyPermissions
            .map(
              (permission) =>
                `${permission.studyLabel} (${permission.access.replace(/_/g, " ")})`,
            )
            .join("; ")
        : "No study access",
    }));

  const configuredExportRows = useMemo(
    () => buildExportRows(visibleUsers),
    [visibleUsers],
  );
  const fullExportRows = useMemo(() => buildExportRows(users), [users]);

  const buildRowMenu = (user: AdminUser): MenuProps["items"] => [
    {
      key: "edit",
      icon: <EditOutlined style={{ fontSize: 18 }} />,
      label: (
        <span className="text-sm text-[#000000d9]">Edit user settings</span>
      ),
      onClick: () => {
        openEditModal(user);
        setOpenMenuId(null);
      },
    },
    {
      key: "deactivate",
      disabled: !user.canDeactivate,
      icon: (
        <UserDeleteOutlined
          style={{
            fontSize: 18,
            color: user.canDeactivate ? "#980000" : undefined,
          }}
        />
      ),
      label: (
        <span
          className="text-sm"
          style={{ color: user.canDeactivate ? "#980000" : undefined }}
        >
          Deactivate
        </span>
      ),
      onClick: () => {
        requestDeactivation(user);
        setOpenMenuId(null);
      },
    },
  ];

  const handleBatchInviteSubmit = async (
    payload: BatchInvitePayload,
  ): Promise<BatchInviteSubmitResult> => {
    setInviteErrorMessage(null);

    const inviteResults = await Promise.allSettled(
      payload.invites.map((invite) =>
        createManagedUserMutation.mutateAsync(
          buildCreateManagedUserPayload(invite, payload.sharedStudyAssignment),
        ),
      ),
    );

    const failedInvites = payload.invites.filter(
      (_, index) => inviteResults[index]?.status === "rejected",
    );

    if (failedInvites.length === 0) {
      setInviteModalOpen(false);
      openSuccessNotification(
        buildInviteSuccessNotification(payload.invites.length),
      );
      return {};
    }

    const firstFailure = inviteResults.find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    const baseMessage =
      firstFailure?.reason instanceof Error
        ? firstFailure.reason.message
        : "Failed to send invite(s). Please try again.";

    const inviteErrorMessage =
      failedInvites.length === payload.invites.length
        ? baseMessage
        : `${baseMessage.replace(/[.\s]*$/, ".")} Retrying will resend only the failed invites.`;

    setInviteErrorMessage(inviteErrorMessage);

    if (failedInvites.length === payload.invites.length) {
      openErrorNotification(buildInviteErrorNotification(inviteErrorMessage));
    } else {
      openErrorNotification(
        buildInvitePartialErrorNotification(
          failedInvites.length,
          payload.invites.length,
        ),
      );
    }

    return {
      failedEmails: failedInvites.map((invite) => invite.email),
    };
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: "User",
      key: "user",
      sorter: (a, b) =>
        getAdminUserDisplayName(a).localeCompare(getAdminUserDisplayName(b)),
      ...textSearchFilter<AdminUser>(
        (record) => `${getAdminUserDisplayName(record)} ${record.email}`,
      ),
      render: (_, user) => (
        <div className="flex flex-col">
          <span className="py-0.5 text-sm text-[#000000d9]">
            {getAdminUserDisplayName(user)}
          </span>
          {getUserSecondaryLine(user) ? (
            <span className="py-0.5 text-sm text-[#00000073]">
              {getUserSecondaryLine(user)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "accountType",
      key: "accountType",
      width: 160,
      sorter: (a, b) => a.accountType.localeCompare(b.accountType),
      filters: [
        { text: "User", value: "USER" },
        { text: "Admin", value: "ADMIN" },
      ],
      onFilter: (value, record) => record.accountType === value,
      render: (accountType: AdminAccountType) => (
        <AccountBadge accountType={accountType} />
      ),
    },
    {
      title: "Invited on",
      dataIndex: "invitedOn",
      key: "invitedOn",
      sorter: (a, b) => a.invitedOn.localeCompare(b.invitedOn),
      ...textSearchFilter<AdminUser>((record) => record.invitedOn),
      render: (invitedOn: string) => (
        <span className="whitespace-nowrap text-base text-[#0F1923]">
          {invitedOn}
        </span>
      ),
    },
    {
      title: "Invited by",
      key: "invitedBy",
      sorter: (a, b) => a.invitedBy.name.localeCompare(b.invitedBy.name),
      filters: invitedByFilters,
      onFilter: (value, record) => record.invitedBy.name === value,
      render: (_, user) =>
        user.invitedBy.isVivo ? (
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2B4752]">
              <img src={vivoLogoWhite} width={12} height={8} alt="Vivo" />
            </div>
            <span className="text-sm">{user.invitedBy.name}</span>
          </div>
        ) : (
          <AvatarWithInitials
            name={user.invitedBy.name}
            avatarSize={20}
            avatarFontSize={10}
            labelFontSize={14}
          />
        ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, user) => (
        <Dropdown
          menu={{ items: buildRowMenu(user) }}
          trigger={["click"]}
          placement="bottomRight"
          open={openMenuId === user.id}
          onOpenChange={(open) => setOpenMenuId(open ? user.id : null)}
        >
          <button
            type="button"
            aria-label={`Actions for ${getUserRowActionLabel(user)}`}
            className={`flex h-6 w-6 items-center justify-center rounded p-1 transition-colors ${
              openMenuId === user.id
                ? "bg-[#0000000f] shadow-[0px_2px_10px_2px_rgba(0,0,0,0.05)]"
                : "hover:bg-[#0000000a]"
            }`}
          >
            <EllipsisOutlined style={{ fontSize: 20, color: "#556C75" }} />
          </button>
        </Dropdown>
      ),
    },
  ];

  const handleOpenInviteModal = () => {
    setInviteErrorMessage(null);
    setInviteModalOpen(true);
  };

  return (
    <div className="relative flex w-full flex-col items-start p-5">
      {contextHolder}
      {editFlowContextHolder}
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full justify-between gap-4">
          <PageHeader
            title="Users & Access"
            tooltipText={USERS_ACCESS_TOOLTIP}
            className="m-0 text-lg font-semibold text-black"
            dataTestId="admin-users-access-header-tooltip"
          />

          <div className="flex items-center gap-2">
            <Button
              type="default"
              icon={<UsergroupAddOutlined style={{ fontSize: 18 }} />}
              className="h-8 rounded border-[#06709B] bg-[#D2EFF9] font-medium text-[#06709B] hover:bg-[#c2e8f6]! hover:text-[#06709B]! hover:border-[#06709B]!"
              onClick={handleOpenInviteModal}
            >
              Invite new user(s)
            </Button>
            <AdminDownloadMenu
              configuredRows={configuredExportRows}
              fullRows={fullExportRows}
              fileName="admin_users_access"
              dataTestId="admin-users-export"
            />
            <GuideButton pageName="/wiki/guides/admin-users-access" />
          </div>
        </div>

        <FetchWrapperState
          data={users}
          isLoading={isPageLoading}
          error={pageError}
        >
          {(resolvedUsers) =>
            resolvedUsers.length === 0 ? (
              <section className="rounded bg-white p-6 text-sm text-[#556C75] shadow-[2px_2px_5px_rgba(0,0,0,0.03)]">
                No users found yet.
              </section>
            ) : (
              <Table<AdminUser>
                data-testid="admin-users-access-table"
                dataSource={resolvedUsers}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
                onChange={(_, filters, __, extra) => {
                  hasActiveTableFilterRef.current = Object.values(filters).some(
                    (value) => Array.isArray(value) && value.length > 0,
                  );
                  setVisibleUsers(extra.currentDataSource);
                }}
              />
            )
          }
        </FetchWrapperState>
      </div>

      <EditUserSettingsModal
        user={editingUser}
        open={editingUser !== null}
        isSubmitting={isEditUserSubmitting}
        submitError={editErrorMessage}
        isLastAdmin={editingUser?.accountType === "ADMIN" && adminCount <= 1}
        roleOptionsByTrialId={roleOptionsByTrialId}
        onClose={closeEditModal}
        onSubmit={handleEditUserSubmit}
      />

      <DeactivateUserModal
        user={deactivationIntent?.user ?? null}
        variant={deactivationIntent?.variant}
        isSubmitting={isDeactivateSubmitting}
        submitError={deactivateErrorMessage}
        onClose={closeDeactivationIntent}
        onConfirm={handleDeactivateUser}
      />

      <BatchInviteUsersModal
        open={inviteModalOpen}
        existingEmails={users.map((user) => user.email)}
        studyOptions={managedStudyOptions}
        isSubmitting={createManagedUserMutation.isPending}
        submitError={inviteErrorMessage}
        onCancel={() => setInviteModalOpen(false)}
        onSubmit={handleBatchInviteSubmit}
      />
    </div>
  );
}
